import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import { X, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Maximize, Minimize, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Convert video path to streaming URL for proper seeking support
const getStreamingUrl = (src) => {
  if (!src) return '';
  
  // Extract filename from various path formats
  let filename = '';
  
  if (src.includes('/uploads/videos/')) {
    filename = src.split('/uploads/videos/')[1];
  } else if (src.includes('/api/uploads/videos/')) {
    filename = src.split('/api/uploads/videos/')[1];
  } else if (src.includes('/stream/video/')) {
    // Already a streaming URL
    return src.startsWith('http') ? src : `${BACKEND_URL}${src}`;
  }
  
  if (filename) {
    return `${BACKEND_URL}/api/stream/video/${filename}`;
  }
  
  // Fallback: prepend backend URL if it's a relative path
  if (src.startsWith('/')) {
    return `${BACKEND_URL}${src}`;
  }
  
  return src;
};

const formatTime = (sec) => {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * SimpleVideoPlayer - Professional video player with custom controls
 */
const SimpleVideoPlayer = memo(({ 
  videoSrc, 
  title, 
  subtitle,
  allowSeek = true,
  autoPlay = false,
  onVideoEnd, 
  onClose 
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const controlsTimeout = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);

  const fullSrc = getStreamingUrl(videoSrc);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  // Seek to specific time
  const seekTo = useCallback((time) => {
    const video = videoRef.current;
    if (!video || !allowSeek) return;
    video.currentTime = Math.max(0, Math.min(time, duration));
  }, [allowSeek, duration]);

  // Skip forward/backward
  const skipForward = useCallback(() => {
    if (!allowSeek) return;
    seekTo(currentTime + 10);
  }, [allowSeek, currentTime, seekTo]);

  const skipBackward = useCallback(() => {
    if (!allowSeek) return;
    seekTo(currentTime - 10);
  }, [allowSeek, currentTime, seekTo]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Handle progress bar click
  const handleProgressClick = (e) => {
    if (!allowSeek || !progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    seekTo(percent * duration);
  };

  // Show controls temporarily
  const showControlsTemporarily = () => {
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying && !ended) setShowControls(false);
    }, 3000);
  };

  // Auto-play on mount
  useEffect(() => {
    if (autoPlay && videoRef.current && isReady) {
      videoRef.current.play().catch(() => {});
    }
  }, [autoPlay, isReady]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoadedMetadata = () => {
      setDuration(video.duration);
      setIsReady(true);
    };
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setEnded(true);
      setIsPlaying(false);
      setTimeout(() => onVideoEnd?.(), 800);
    };
    const onWaiting = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('volumechange', onVolumeChange);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('volumechange', onVolumeChange);
    };
  }, [onVideoEnd]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'Escape':
          e.preventDefault();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            onClose?.();
          }
          break;
        case 'ArrowLeft':
          if (allowSeek) { e.preventDefault(); skipBackward(); }
          break;
        case 'ArrowRight':
          if (allowSeek) { e.preventDefault(); skipForward(); }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (videoRef.current) videoRef.current.volume = Math.min(1, videoRef.current.volume + 0.1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (videoRef.current) videoRef.current.volume = Math.max(0, videoRef.current.volume - 0.1);
          break;
        case 'KeyF':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'KeyM':
          e.preventDefault();
          if (videoRef.current) videoRef.current.muted = !videoRef.current.muted;
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, skipForward, skipBackward, toggleFullscreen, allowSeek, onClose]);

  // Fullscreen change handler
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black"
      onMouseMove={showControlsTemporarily}
      onClick={(e) => {
        // Click on video area toggles play
        if (e.target === e.currentTarget || e.target.tagName === 'VIDEO') {
          togglePlay();
        }
      }}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={fullSrc}
        className="w-full h-full object-contain"
        playsInline
        preload="auto"
        onClick={togglePlay}
      />

      {/* Header - Title and Close */}
      <div className={`absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg drop-shadow-lg">{title}</h2>
            {subtitle && <p className="text-white/70 text-sm">{subtitle}</p>}
          </div>
          {onClose && (
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {/* Center Play Button (when paused) */}
      {!isPlaying && !ended && isReady && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <button
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="p-6 rounded-full bg-sky-600/90 hover:bg-sky-700 transition-all transform hover:scale-105 shadow-2xl pointer-events-auto"
          >
            <Play className="h-12 w-12 text-white" fill="white" />
          </button>
        </div>
      )}

      {/* Buffering indicator */}
      {isBuffering && !ended && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="animate-spin h-12 w-12 border-4 border-sky-500 border-t-transparent rounded-full"></div>
        </div>
      )}

      {/* Loading state */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center text-white">
            <div className="animate-spin h-12 w-12 border-4 border-sky-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading video...</p>
          </div>
        </div>
      )}

      {/* Video ended overlay */}
      {ended && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center text-white">
            <CheckCircle2 className="h-20 w-20 text-emerald-400 mx-auto mb-4" />
            <p className="text-2xl font-bold">Video Complete!</p>
          </div>
        </div>
      )}

      {/* Bottom Controls */}
      <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        {/* Progress Bar */}
        {allowSeek && (
          <div 
            ref={progressRef}
            className="w-full h-1.5 bg-white/30 rounded-full mb-4 cursor-pointer group"
            onClick={handleProgressClick}
          >
            <div 
              className="h-full bg-sky-500 rounded-full relative transition-all"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          {/* Left Controls */}
          <div className="flex items-center gap-2">
            {/* Skip Back */}
            {allowSeek && (
              <button
                onClick={(e) => { e.stopPropagation(); skipBackward(); }}
                className="p-2 text-white hover:text-sky-400 transition-colors"
                title="Skip back 10s"
              >
                <SkipBack className="h-5 w-5" />
              </button>
            )}

            {/* Play/Pause */}
            <button
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              className="p-2 text-white hover:text-sky-400 transition-colors"
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>

            {/* Skip Forward */}
            {allowSeek && (
              <button
                onClick={(e) => { e.stopPropagation(); skipForward(); }}
                className="p-2 text-white hover:text-sky-400 transition-colors"
                title="Skip forward 10s"
              >
                <SkipForward className="h-5 w-5" />
              </button>
            )}

            {/* Volume */}
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                if (videoRef.current) videoRef.current.muted = !videoRef.current.muted;
              }}
              className="p-2 text-white hover:text-sky-400 transition-colors"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>

            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                e.stopPropagation();
                if (videoRef.current) {
                  videoRef.current.volume = Number(e.target.value);
                  videoRef.current.muted = false;
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-20 h-1 accent-sky-500 cursor-pointer"
            />

            {/* Time */}
            <span className="text-white/80 text-sm ml-2">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-2">
            {/* Fullscreen */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
              className="p-2 text-white hover:text-sky-400 transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Keyboard hints */}
        <div className="text-center mt-2">
          <p className="text-white/40 text-xs">
            Space=Play/Pause {allowSeek && '· ←→=Skip 10s'} · ↑↓=Volume · M=Mute · F=Fullscreen · Esc=Close
          </p>
        </div>
      </div>
    </div>
  );
});

SimpleVideoPlayer.displayName = 'SimpleVideoPlayer';

export default SimpleVideoPlayer;
